import React, { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  CircularProgress,
  Alert,
  Card,
  CardContent,
} from "@mui/material";
import getApiBase from "./getApiBase";

const TestAccountShopInviteModal = ({ open, onClose, userEmail, onSuccess }) => {
  const [shops, setShops] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedShopId, setSelectedShopId] = useState(null);
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    if (open) {
      fetchClaimedTestShops();
    }
  }, [open]);

  const fetchClaimedTestShops = async () => {
    setLoading(true);
    setError("");
    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${getApiBase()}/api/qa/test/claimed-test-shops`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to fetch shops");
      }

      const data = await response.json();
      setShops(data.shops || []);
    } catch (err) {
      setError("Failed to load shops. Please try again.");
      console.error("Error fetching shops:", err);
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptInvite = async () => {
    if (!selectedShopId) {
      setError("Please select a shop to join");
      return;
    }

    setAccepting(true);
    setError("");

    try {
      const token = localStorage.getItem("token");
      const response = await fetch(`${getApiBase()}/api/qa/test/accept-invite`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ shopId: selectedShopId }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to accept invite");
      }

      const data = await response.json();
      onSuccess(data.shop);
    } catch (err) {
      setError(err.message || "Failed to accept invite");
      console.error("Error accepting invite:", err);
    } finally {
      setAccepting(false);
    }
  };

  const selectedShop = shops.find((s) => s.id === selectedShopId);

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Typography variant="h6" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
          🧪 Join a Test Shop
        </Typography>
        <Typography variant="body2" sx={{ mt: 1, color: "#6b7280" }}>
          Select a shop that has been claimed by a test owner to join as a mechanic.
        </Typography>
      </DialogTitle>
      <DialogContent>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError("")}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 3 }}>
            <CircularProgress />
          </Box>
        ) : shops.length === 0 ? (
          <Alert severity="info">
            No test shops available. Please ask a test owner to claim a shop first.
          </Alert>
        ) : (
          <Box sx={{ maxHeight: "400px", overflowY: "auto" }}>
            {shops.map((shop) => (
              <Card
                key={shop.id}
                sx={{
                  mb: 2,
                  cursor: "pointer",
                  border: selectedShopId === shop.id ? "2px solid #4285f4" : "1px solid #e5e7eb",
                  "&:hover": {
                    borderColor: "#4285f4",
                  },
                }}
                onClick={() => setSelectedShopId(shop.id)}
              >
                <CardContent>
                  <Typography variant="h6" sx={{ fontFamily: "Poppins, sans-serif", fontWeight: 600 }}>
                    {shop.shop_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                    {shop.address}
                  </Typography>
                  {shop.phone && (
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                      📞 {shop.phone}
                    </Typography>
                  )}
                  {shop.rating && (
                    <Typography variant="body2" sx={{ color: "#6b7280", mt: 0.5 }}>
                      ⭐ {shop.rating}
                    </Typography>
                  )}
                  {shop.owner_email && (
                    <Typography variant="body2" sx={{ color: "#4285f4", mt: 0.5 }}>
                      Owner: {shop.owner_email}
                    </Typography>
                  )}
                </CardContent>
              </Card>
            ))}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} disabled={accepting}>
          Cancel
        </Button>
        <Button
          onClick={handleAcceptInvite}
          variant="contained"
          disabled={!selectedShopId || accepting}
          sx={{ backgroundColor: "#4285f4" }}
        >
          {accepting ? <CircularProgress size={20} /> : "Join Shop"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default TestAccountShopInviteModal;

